import { cloneElement, isValidElement, memo } from 'react'

import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'

import { stylePropType } from '~/src/Lib/PropTypes'
import { dtLabelSanitizer, EMPTY_OBJECT } from '~/src/Lib/Utils'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import { dataTypeMap } from '~/src/UI/Icons'

const defaultStyle = {
  textTransform: 'none',
}

const DEFAULT_ICON_STYLE = { marginRight: '0.5rem' }

const getStyle = memoizeOne(({ dataType = EMPTY_OBJECT, style = EMPTY_OBJECT }) => {
  const { color = 'inherit' } = dataType
  return { ...defaultStyle, color, ...style }
})

/**
 * A component for rendering data type names
 *
 * @example
 *   ;<div className="default-display">
 *     <T noWrap>
 *       <DataTypeName icon dataType={{ name: 'EC<sub>pw</sub>', key: 'pore_ec', color: 'yellow' }} />
 *     </T>
 *     <T noWrap>
 *       <DataTypeName icon dataType={{ name: 'Water Potential', key: 'water_potential', color: 'cyan' }} />
 *     </T>
 *     <T noWrap>
 *       <DataTypeName icon dataType={{ name: 'VPD', key: 'vpd', color: 'limegreen' }} />
 *     </T>
 *     <DataTypeName
 *       icon
 *       caption={
 *         <T.Tiny color="inherit" component="span" display="inline">
 *           3.56 <small>dS/m</small>
 *         </T.Tiny>
 *       }
 *       component={({ style, ...props }) => (
 *         <Box
 *           component={T.Tiny}
 *           display="flex"
 *           flexDirection="column"
 *           alignItems="center"
 *           style={{ ...style, gap: 2 }}
 *           {...props}
 *         />
 *       )}
 *       dataType={{ name: 'EC<sub>pw</sub>', key: 'pore_ec', color: 'yellow' }}
 *       iconStyle={{ margin: 0 }}
 *     />
 *   </div>
 *
 * @component
 */
const DataTypeName = ({
  caption: captionRaw = null,
  Component = 'span',
  Icon = undefined,
  iconStyle = DEFAULT_ICON_STYLE,
  name,
  style = EMPTY_OBJECT,
  ...rest
}) => {
  const content = <span className="datatype-name" dangerouslySetInnerHTML={{ __html: dtLabelSanitizer(name) }} />
  let caption = captionRaw
  if (isValidElement(captionRaw)) {
    caption = cloneElement(captionRaw, {
      className: captionRaw.props.className ? `${captionRaw.props.className} datatype-caption` : 'datatype-caption',
    })
  }
  if (caption && typeof caption === 'string') {
    caption = <span className="datatype-caption">{caption}</span>
  }

  return (
    <Component {...rest} style={style}>
      {caption}
      {Icon ? <Icon className="datatype-icon" color="inherit" style={iconStyle} /> : null}
      {content}
    </Component>
  )
}

DataTypeName.propTypes = {
  caption: PropTypes.string,
  Component: PropTypes.elementType,
  Icon: PropTypes.elementType,
  iconStyle: stylePropType,
  name: PropTypes.string.isRequired,
  style: stylePropType,
}

const Wrapped = ({ component, icon, name, dataType: dataTypeProp, style, ...passthru }) => {
  const { dataTypes } = useConnectModern('selectDataTypes')
  const dataType = typeof dataTypeProp === 'string' ? dataTypes[dataTypeProp] : dataTypeProp
  const { [icon && dataType && dataType.key?.toLowerCase()]: Icon } = dataTypeMap
  return (
    <DataTypeName
      {...passthru}
      Component={component}
      Icon={Icon}
      name={name ?? dataType?.name}
      style={getStyle({ dataType, style })}
    />
  )
}

const Memoized = memo(Wrapped)
export default Memoized
